import { useEffect, useState, useCallback } from "react";
import { fmtDate } from "../lib/date";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { CaretLeft, Plus, Trash, CheckCircle, Prohibit, IdentificationCard } from "@phosphor-icons/react";

const DOT = { green: "bg-emerald-500", blue: "bg-blue-500", yellow: "bg-amber-400", red: "bg-red-600", grey: "bg-slate-300" };
const STATE_LABEL = { green: "Compliant", blue: "Verify", yellow: "Expiring", red: "Non-compliant", grey: "N/A" };
const LEVELS = [["required", "Required"], ["optional", "Optional / Relevant"], ["na", "Not required / N/A"]];

function StateChip({ state }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase px-2 py-0.5 rounded text-white ${state === "yellow" || state === "grey" ? "text-slate-900" : ""} ${DOT[state]}`}>
      {STATE_LABEL[state] || state}
    </span>
  );
}

function DeptCards({ onOpen }) {
  const [cards, setCards] = useState(null);
  useEffect(() => { api.get("/compliance/departments").then((r) => setCards(r.data)).catch(() => setCards([])); }, []);
  if (!cards) return <p className="text-slate-500">Loading compliance…</p>;
  if (cards.length === 0) return <p className="text-slate-400">No departments yet. Create them in Admin → Organisation → Departments, then set required competencies here.</p>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((c) => (
        <button key={c.department_id || "none"} data-testid={`dept-card-${c.department_id || "none"}`}
          onClick={() => onOpen(c)}
          className="text-left bg-white border-2 border-slate-200 rounded-lg p-4 hover:border-blue-500 transition-colors duration-100">
          <div className="font-display text-xl font-black text-slate-900">{c.name}</div>
          <div className="font-body text-sm text-slate-500 mb-3">{c.total} {c.total === 1 ? "person" : "people"}</div>
          <div className="flex flex-wrap gap-2">
            {c.green > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold"><span className={`w-2.5 h-2.5 rounded-full ${DOT.green}`} />{c.green} compliant</span>}
            {c.blue > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold"><span className={`w-2.5 h-2.5 rounded-full ${DOT.blue}`} />{c.blue} verify</span>}
            {c.yellow > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold"><span className={`w-2.5 h-2.5 rounded-full ${DOT.yellow}`} />{c.yellow} expiring</span>}
            {c.red > 0 && <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700"><span className={`w-2.5 h-2.5 rounded-full ${DOT.red}`} />{c.red} non-compliant</span>}
            {c.total === 0 && <span className="text-xs text-slate-400">No people</span>}
          </div>
        </button>
      ))}
    </div>
  );
}

function DeptRequirements({ dept, ctypes }) {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("administrator", "training_manager", "supervisor");
  const [reqs, setReqs] = useState([]);
  const [ct, setCt] = useState("");
  const [level, setLevel] = useState("required");
  const load = useCallback(() => {
    if (!dept.department_id) return;
    api.get(`/compliance/requirements?scope=department&scope_id=${dept.department_id}`).then((r) => setReqs(r.data)).catch(() => {});
  }, [dept.department_id]);
  useEffect(() => { load(); }, [load]);
  if (!dept.department_id) return null;

  const add = async () => {
    if (!ct) return;
    try { await api.post("/compliance/requirements", { scope: "department", scope_id: dept.department_id, competency_type_id: ct, level }); setCt(""); load(); toast.success("Requirement saved"); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/compliance/requirements/${id}`); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="bg-white border-2 border-slate-200 rounded-lg p-4" data-testid="dept-requirements">
      <h3 className="font-body font-semibold text-slate-800 mb-2">Competencies for {dept.name}</h3>
      <div className="flex flex-col gap-1 mb-3">
        {reqs.length === 0 && <p className="text-slate-400 text-sm">No competencies configured for this department yet.</p>}
        {reqs.map((r) => (
          <div key={r.id} data-testid={`dept-req-${r.id}`} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
            <span className="font-body text-slate-700 font-semibold">{r.competency_name}</span>
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.level === "required" ? "bg-slate-900 text-white" : r.level === "optional" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"}`}>{r.level === "na" ? "N/A" : r.level}</span>
            {canManage && <button data-testid={`del-dept-req-${r.id}`} onClick={() => del(r.id)} className="ml-auto text-red-600"><Trash size={14} weight="bold" /></button>}
          </div>
        ))}
      </div>
      {canManage && (
        <div className="flex gap-2 flex-wrap border-t border-slate-100 pt-2">
          <select data-testid="dept-req-ct" className="h-9 border border-slate-300 rounded px-2 text-sm flex-1 min-w-40" value={ct} onChange={(e) => setCt(e.target.value)}>
            <option value="">Competency…</option>{ctypes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="dept-req-level" className="h-9 border border-slate-300 rounded px-2 text-sm" value={level} onChange={(e) => setLevel(e.target.value)}>
            {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Button data-testid="add-dept-req" onClick={add} className="h-9 bg-slate-900 font-semibold"><Plus size={14} weight="bold" className="mr-1" />Add</Button>
        </div>
      )}
    </div>
  );
}

function DeptPeople({ dept, onOpenPerson, ctypes }) {
  const [people, setPeople] = useState(null);
  useEffect(() => {
    api.get(`/compliance/departments/${dept.department_id || "none"}/people`).then((r) => setPeople(r.data)).catch(() => setPeople([]));
  }, [dept.department_id]);
  return (
    <div className="flex flex-col gap-4">
      <DeptRequirements dept={dept} ctypes={ctypes} />
      <div className="bg-white border-2 border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="dept-people">
        {!people ? <p className="text-slate-500 p-4">Loading…</p> : people.length === 0 ? <p className="text-slate-400 p-4">No people in this department.</p> :
          people.map((p) => (
            <button key={p.profile_id} data-testid={`comp-person-${p.profile_id}`} onClick={() => onOpenPerson(p)}
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
              <span className={`w-3.5 h-3.5 rounded-full ${DOT[p.state]}`} />
              <span className="font-body font-semibold text-slate-800 flex-1">{p.name}</span>
              <StateChip state={p.state} />
            </button>
          ))}
      </div>
    </div>
  );
}

function PersonDetail({ person, ctypes, onBack }) {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("administrator", "training_manager", "supervisor");
  const [d, setD] = useState(null);
  const [grantCt, setGrantCt] = useState("");
  const load = useCallback(() => { api.get(`/compliance/person/${person.profile_id}`).then((r) => setD(r.data)).catch(() => {}); }, [person.profile_id]);
  useEffect(() => { load(); }, [load]);

  const setLevel = async (ctid, level) => {
    try { await api.post("/compliance/requirements", { scope: "person", scope_id: person.profile_id, competency_type_id: ctid, level }); load(); toast.success("Updated"); }
    catch (e) { toast.error("Failed"); }
  };
  const grant = async (ctid) => {
    if (!ctid) return;
    try { await api.post("/training/competencies", { profile_id: person.profile_id, competency_type_id: ctid, achieved_date: new Date().toISOString().slice(0, 10) }); setGrantCt(""); load(); toast.success("Added (awaiting verification)"); }
    catch (e) { toast.error("Failed"); }
  };
  const verify = async (cid) => { try { await api.post(`/training/competencies/${cid}/verify`); load(); toast.success("Verified"); } catch (e) { toast.error("Failed"); } };
  const withdraw = async (cid) => { try { await api.post(`/training/competencies/${cid}/withdraw`, {}); load(); toast.success("Withdrawn"); } catch (e) { toast.error("Failed"); } };

  if (!d) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="flex flex-col gap-4" data-testid="person-compliance-detail">
      <div className="flex items-center gap-3">
        <button data-testid="person-back" onClick={onBack} className="text-slate-600 inline-flex items-center gap-1 font-semibold"><CaretLeft size={16} weight="bold" />Back</button>
        <h2 className="font-display text-2xl font-black text-slate-900">{d.name}</h2>
        <StateChip state={d.state} />
      </div>

      <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
        <h3 className="font-body font-semibold text-slate-800 mb-1 inline-flex items-center gap-2"><IdentificationCard size={18} weight="bold" />Authorisations &amp; Competencies</h3>
        <p className="text-xs text-slate-500 mb-3">Required items are assessed for compliance. Use N/A to exempt someone you won't be training on an item.</p>
        <div className="flex flex-col gap-1.5">
          {d.items.length === 0 && <p className="text-slate-400 text-sm">Nothing relevant configured for this person's department/role yet.</p>}
          {d.items.map((it) => (
            <div key={it.competency_type_id} data-testid={`comp-item-${it.competency_type_id}`} className="flex items-center gap-2 text-sm border border-slate-100 rounded px-2 py-1.5">
              <span className={`w-3 h-3 rounded-full ${DOT[it.colour]}`} />
              <span className="font-body font-semibold text-slate-800">{it.name}</span>
              {it.is_authorisation && <span className="text-[9px] font-bold uppercase bg-purple-100 text-purple-700 px-1 rounded">auth</span>}
              <span className="text-[10px] uppercase text-slate-400">{it.level === "na" ? "N/A" : it.level}{it.source !== "department" ? ` · ${it.source}` : ""}</span>
              {it.expiry_date && <span className="text-xs text-slate-500">exp {fmtDate(it.expiry_date)}</span>}
              <span className="ml-auto text-[10px] font-bold uppercase text-slate-500">{it.state.replace(/_/g, " ")}</span>
              {canManage && (
                <div className="flex items-center gap-1">
                  {it.competency_id && it.verification_status !== "verified" && it.state !== "missing" && <button data-testid={`verify-${it.competency_id}`} onClick={() => verify(it.competency_id)} className="text-emerald-700 text-xs font-bold">Verify</button>}
                  {it.competency_id && it.state !== "missing" && <button data-testid={`withdraw-${it.competency_id}`} onClick={() => withdraw(it.competency_id)} className="text-red-600 text-xs font-bold">Withdraw</button>}
                  {!it.competency_id && <button data-testid={`grant-item-${it.competency_type_id}`} onClick={() => grant(it.competency_type_id)} className="text-blue-600 text-xs font-bold">Grant</button>}
                  <select data-testid={`level-${it.competency_type_id}`} value={it.level} onChange={(e) => setLevel(it.competency_type_id, e.target.value)} className="h-7 border border-slate-300 rounded px-1 text-xs">
                    {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
        {canManage && (
          <div className="flex gap-2 mt-3 border-t border-slate-100 pt-3 flex-wrap">
            <select data-testid="grant-ct" className="h-9 border border-slate-300 rounded px-2 text-sm flex-1 min-w-48" value={grantCt} onChange={(e) => setGrantCt(e.target.value)}>
              <option value="">Grant a competency / authorisation…</option>
              {ctypes.map((c) => <option key={c.id} value={c.id}>{c.name}{c.is_authorisation ? " (auth)" : ""}</option>)}
            </select>
            <Button data-testid="grant-btn" onClick={() => grant(grantCt)} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold"><Plus size={14} weight="bold" className="mr-1" />Grant</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DeptCompliancePage() {
  const [ctypes, setCtypes] = useState([]);
  const [dept, setDept] = useState(null);
  const [person, setPerson] = useState(null);
  useEffect(() => { api.get("/config/competency-types").then((r) => setCtypes(r.data)).catch(() => {}); }, []);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Compliance by Department</h1>
        <div className="flex gap-3 text-sm">
          <Link to="/training" className="text-blue-600 font-semibold" data-testid="link-matrix">Detailed matrix →</Link>
          <Link to="/compliance/roles" className="text-blue-600 font-semibold" data-testid="link-roles">Role readiness →</Link>
        </div>
      </div>

      {!dept && <DeptCards onOpen={(c) => { setDept(c); setPerson(null); }} />}

      {dept && !person && (
        <div className="flex flex-col gap-3">
          <button data-testid="dept-back" onClick={() => setDept(null)} className="text-slate-600 inline-flex items-center gap-1 font-semibold w-fit"><CaretLeft size={16} weight="bold" />All departments</button>
          <h2 className="font-display text-2xl font-black text-slate-900">{dept.name}</h2>
          <DeptPeople dept={dept} ctypes={ctypes} onOpenPerson={setPerson} />
        </div>
      )}

      {dept && person && <PersonDetail person={person} ctypes={ctypes} onBack={() => setPerson(null)} />}
    </div>
  );
}
